import { Alert, Button, Modal } from 'antd'
import { OntologyValueType } from 'features/dynamicOntology'
import { useTicketUpdate } from 'features/libra/ticket/TicketUpdateContext'
import {
  LibraWorflowStepComponentProps,
  UseValidateConfigHook,
} from 'features/libra/workflow/workflowStepsApi'
import React, { FC, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import cantileverTheme from 'styles/cantileverTheme'

import {
  ConditionalFormConfig,
  ConditionalFormConfigSchema,
  ConditionalFormTicketPropsSchema,
} from './conditionalForm/api/conditionalFormApi'
import { ConditionalFormSectionView } from './conditionalForm/conditionalFormSection/ConditionalFormSection'
import { isConditionalFormStepComplete } from './conditionalForm/conditionalFormStepCompletion'
import { FieldValueLookupProvider } from './conditionalForm/FieldValueLookupContext'
import { useConditionalFormTicketFieldValues } from './conditionalForm/useConditionalFormTicketFieldValues'

export const useValidateConfig: UseValidateConfigHook = ({ config }) => {
  return useMemo(
    () => ConditionalFormConfigSchema.safeParse(config).success,
    [config]
  )
}

export const Component: FC<
  LibraWorflowStepComponentProps<ConditionalFormConfig>
> = ({ config, setValidationResult }) => {
  const [isConfirmSaveVisible, setIsConfirmSaveVisible] = useState(false)

  const { ontologyFields, sections, form, lookups } = useMemo(() => {
    return ConditionalFormConfigSchema.parse(config)
  }, [config])

  const { ticketProps } = useTicketUpdate()
  const { fieldValues } = useMemo(
    () => ConditionalFormTicketPropsSchema.parse(ticketProps),
    [ticketProps]
  )

  const {
    fieldValuesWithDefaults,
    setFieldValues,
    visibleSections,
    hasChanges,
    handleCancel,
    handleSave,
    isLoading,
  } = useConditionalFormTicketFieldValues({
    ontologyFields,
    sections,
    form,
  })

  const isStepComplete = useMemo(() => {
    return isConditionalFormStepComplete({ config, fieldValues })
  }, [config, fieldValues])

  useEffect(() => {
    setValidationResult(
      isStepComplete
        ? undefined
        : {
            status: 'Error',
            message: 'Step incomplete - please fill in all required fields',
          }
    )
  }, [isStepComplete, setValidationResult])

  return (
    <FormContainer>
      <StyledAlert
        message={hasChanges ? 'Save Changes?' : 'No Changes'}
        type={hasChanges ? 'warning' : 'success'}
        showIcon
        action={
          hasChanges && (
            <BannerActions>
              <CancelButton
                type='dashed'
                size='small'
                disabled={isLoading}
                onClick={handleCancel}>
                Cancel
              </CancelButton>
              <SaveButton
                type='primary'
                size='small'
                disabled={isLoading}
                onClick={() => setIsConfirmSaveVisible(true)}>
                Save
              </SaveButton>
            </BannerActions>
          )
        }
      />
      <Modal
        title='Confirm Save'
        loading={isLoading}
        open={isConfirmSaveVisible}
        onOk={async () => {
          await handleSave()
          setIsConfirmSaveVisible(false)
        }}
        onCancel={() => setIsConfirmSaveVisible(false)}
        okText='Save'
        cancelText='Cancel'
        confirmLoading={isLoading}>
        Are you sure you want to save these changes?
      </Modal>
      <FieldValueLookupProvider config={lookups}>
        {visibleSections.map((section) => (
          <ConditionalFormSectionView
            key={section.id}
            config={section}
            ontologyFields={ontologyFields}
            virtualObject={
              fieldValuesWithDefaults as unknown as OntologyValueType
            }
            setVirtualObject={setFieldValues}
          />
        ))}
      </FieldValueLookupProvider>
    </FormContainer>
  )
}

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`

const StyledAlert = styled(Alert)`
  min-height: 48px;

  .ant-alert-content {
    display: flex;
    align-items: center;
  }
`

const BannerActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const CancelButton = styled(Button)`
  background-color: ${cantileverTheme.colors.grayscale['500']} !important;
`

const SaveButton = styled(Button)`
  background-color: ${cantileverTheme.colors.OceanBlue} !important;
`
